import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { usePathParam, useSecondOrderCallback, useSwitch, useViewport } from '@chilecompra/react-kit'
import {
  Breadcrumb,
  Breadcrumbs,
  Button,
  Popover,
  PopoverMenuItem,
  Spinner,
  Switch,
  Tag,
  Toast,
  Tooltip,
  Typography
} from '@chilecompra/react-kit/components'
import { REACT_APP_SEARCH_OC_URL } from '../../config/settings/environment'
import routes from '../../config/settings/routes'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetProviderThunk } from './ProviderSection.actions'
import ProviderDialog from '../../components/Dialogs/ProviderDialog'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import {
  Action,
  ActionLink,
  Anchor,
  AnchorIcon,
  AnchorText,
  Card,
  Content,
  Datum,
  ErrorMessage,
  GridContainer,
  GridItem,
  HelpIcon,
  Info,
  Jumbotron,
  Label,
  Line,
  Link,
  Title,
  Root,
  Strong,
  Subtitle,
  TagWrapper,
  UserIcon,
  Value
} from './ProviderSection.styles'

/**
 * The ProviderSection's container.
 */
const ProviderSection = props => {
  const { children } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const dni = usePathParam('dni') || ''
  const { amount, amountUSD, biddings, error, loading, link, name, orders, ranking } = useSelector(
    state => state.providerSection
  )
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = useMemo(() => generateYearsBetween(endYear, startYear), [])
  const [helpOpen, setHelpOpen] = useState(false)
  const [yearsOpen, setYearsOpen] = useState(false)
  const [yearValue, setYearValue] = useState(endYear)
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const { size } = useViewport()
  const createHandleYearChange = useSecondOrderCallback(
    (_, updatedValue) => {
      setYearValue(updatedValue)
      setYearsOpen(false)
      dispatch(onGetProviderThunk({ dni, year: updatedValue }))
    },
    [dni]
  )
  const handleCloseHelp = useCallback(() => setHelpOpen(false), [])
  const handleOpenHelp = useCallback(() => setHelpOpen(true), [])
  const handleCloseYears = useCallback(() => setYearsOpen(false), [])
  const handleOpenYears = useCallback(() => setYearsOpen(true), [])
  const handleRedirectToHome = useCallback(() => navigate(routes.root, { replace: true }), [])
  const handleRedirectToDetails = useCallback(() => window.open(link, '_blank'))

  useEffect(() => {
    if (!dni || !endYear || initialLoading) {
      return
    }

    setYearValue(endYear)
    dispatch(onGetProviderThunk({ dni, year: endYear }))
  }, [dni, endYear, initialLoading])

  return (
    <Root height={loading || !!error ? '100%' : 'auto'}>
      {(initialLoading || loading) && (
        <GridContainer alignItems="center" height="100%">
          <GridItem align="center" xs={12}>
            <Spinner />
          </GridItem>
        </GridContainer>
      )}
      {!!error && !initialLoading && !loading && (
        <GridContainer alignItems="center" height="100%">
          <GridItem align="center" xs={12}>
            <ErrorMessage>
              No existe una ficha de organismo proveedor asociada al RUT “<Strong>{dni}</Strong>”
            </ErrorMessage>
            <Button color="primary" onClick={handleRedirectToHome}>
              Volver al Inicio
            </Button>
          </GridItem>
        </GridContainer>
      )}
      {!error && !initialLoading && !loading && (
        <>
          <GridContainer>
            <GridItem xs={12}>
              <Jumbotron height="314px" linksColor={theme.palette.red[90]} pointsColor={theme.palette.red[90]}>
                <Breadcrumbs>
                  <Breadcrumb to={routes.root} variant="link">
                    Datos Abiertos
                  </Breadcrumb>
                  <Breadcrumb>Proveedores</Breadcrumb>
                </Breadcrumbs>
                <TagWrapper>
                  <Tag color="error" text="Proveedor" />
                </TagWrapper>
                <Title>{name}</Title>
                <Subtitle>{dni}</Subtitle>
              </Jumbotron>
            </GridItem>
          </GridContainer>
          <Content>
            <GridContainer margin="0 auto" maxWidth="950px">
              <GridItem margin="24px 0 0" md={8} xs={12}>
                <Typography display="inline" fontSize="16px" margin="0 10px 0 0" variant="body2">
                  Cifras acumuladas durante el año
                </Typography>
                <Popover
                  anchor={
                    <Anchor>
                      <AnchorText>{yearValue}</AnchorText>
                      <AnchorIcon open={yearsOpen} />
                    </Anchor>
                  }
                  open={yearsOpen}
                  onClose={handleCloseYears}
                  onOpen={handleOpenYears}
                >
                  {years.map(year => (
                    <PopoverMenuItem enableClose key={year.value} onClick={createHandleYearChange(year.value)}>
                      {year.name}
                    </PopoverMenuItem>
                  ))}
                </Popover>
              </GridItem>
            </GridContainer>
            <GridContainer margin="0 auto" maxWidth="950px">
              <GridItem margin="7px 0 0" xs={12}>
                <Card color="blank" elevation="1">
                  <GridContainer spacing={1}>
                    <GridItem height="60px" md={4} xs={12}>
                      <Info>
                        <Datum>
                          <Value>${formatAmount(parseInt((currencyValue ? amountUSD : amount) ?? 0, 10))}</Value>
                          <Label margin="7px 0 0">Monto total transado durante el año</Label>
                        </Datum>
                      </Info>
                    </GridItem>
                    <GridItem md={8} xs={12}>
                      <GridContainer>
                        <GridItem height="60px" md={4} xs={12}>
                          <Info>
                            <Line />
                            <Datum>
                              <Value>{formatAmount(parseInt(orders ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Órdenes de compra</Label>
                            </Datum>
                          </Info>
                        </GridItem>
                        <GridItem height="60px" md={4} xs={12}>
                          <Info>
                            <Line />
                            <Datum>
                              <Value>{formatAmount(parseInt(biddings ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Licitaciones adjudicadas</Label>
                            </Datum>
                          </Info>
                        </GridItem>
                        <GridItem height="60px" md={4} xs={12}>
                          <Info>
                            <Line />
                            <Datum>
                              <Value>{formatAmount(parseInt(ranking ?? 0, 10))}.°</Value>
                              <Tooltip
                                content="Posición que ocupa el proveedor calculada en base a su monto transado a nivel nacional."
                                placement="top"
                              >
                                <Label enableUnderline margin="7px 0 0">
                                  Posición a nivel nacional
                                </Label>
                              </Tooltip>
                            </Datum>
                          </Info>
                        </GridItem>
                      </GridContainer>
                    </GridItem>
                  </GridContainer>
                  <GridContainer padding="45px 0 0" spacing={1} alignItems="flex-end">
                    <GridItem md={9} xs={12}>
                      <Action>
                        <ActionLink onClick={handleOpenHelp}>
                          <HelpIcon>?</HelpIcon>
                          En qué se basa la información de este proveedor
                        </ActionLink>
                      </Action>
                      <Action margin="6px 0 0">
                        <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/downloads-page']}>
                          <ActionLink onClick={handleRedirectToDetails}>
                            <UserIcon />
                            Ver ficha pública del proveedor en Mercado Público
                          </ActionLink>
                        </ProtectByFeatures>
                      </Action>
                    </GridItem>
                    <GridItem
                      md={3}
                      textAlign={size.isDownToMedium ? 'center' : 'right'}
                      margin={size.isDownToMedium ? '24px 0 32px' : '0'}
                      xs={12}
                    >
                      <Switch
                        checked={currencyValue}
                        color="primary"
                        labelLeft="CLP"
                        labelRight="USD"
                        onChange={handleChangeCurrency}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </GridItem>
            </GridContainer>
            <GridContainer margin="64px 0" justify="center">
              <GridItem align="center" xs={10}>
                <Toast align="center" disableIcon>
                  Para explorar el listado completo de órdenes de compra del proveedor puedes hacerlo en el{' '}
                  <Link href={REACT_APP_SEARCH_OC_URL} rel="noreferrer" target="_blank">
                    buscador de órdenes de compra
                  </Link>{' '}
                  de Mercado Público.
                </Toast>
              </GridItem>
            </GridContainer>
          </Content>
          {children}
          <ProviderDialog open={helpOpen} onClose={handleCloseHelp} />
        </>
      )}
    </Root>
  )
}

ProviderSection.propTypes = {
  children: PropTypes.node.isRequired
}

export default ProviderSection
